import React from 'react'

// Components
import { DetailView } from './DetailView'
import { RouteParams } from 'constants/routes'
import { RouteComponentProps } from 'react-router-dom'
import { Pagination } from '../types'

// Types
import {
  useGetDetailViewArtboardQuery,
  GetDetailViewArtboardQuery,
  VersionFragment,
  ArtboardDetailInfoFragment,
} from 'gqlTypes'
import { RevisionChangedProps } from 'modules/activity/operations'

// Hooks
import { useQueryRetry } from 'utils/withQueryRetry'
import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { useOnCurrentRevisionChange } from './useOnCurrentRevisionChange'

const getArtboard = (result: { data?: GetDetailViewArtboardQuery }) => {
  const artboard = result?.data?.artboard
  // at some point Apollo (while loading) returns an artboard which contains only
  // a symbol of id, all other properties are non existing
  // this gives a false impression, that the object is there, but it isn't
  return artboard?.identifier ? artboard : undefined
}

interface UseRetryGetDetailViewArtboardQueryProps {
  permanentArtboardShortId: string
  versionShortId?: string
  shareIdentifier: string
  enabled: boolean
}

const useRetryGetDetailViewArtboardQuery = ({
  permanentArtboardShortId,
  versionShortId,
  shareIdentifier,
  enabled = true,
}: UseRetryGetDetailViewArtboardQueryProps) =>
  useQueryRetry(
    useGetDetailViewArtboardQuery({
      shouldInvalidatePrevious: (prev, curr) =>
        prev?.permanentArtboardShortId !== curr?.permanentArtboardShortId,
      // To account for artboard updates we're setting a cache-and-network
      // policy for artboard queries without an explicit version.
      fetchPolicy: versionShortId ? 'cache-first' : 'cache-and-network',
      variables: {
        permanentArtboardShortId: permanentArtboardShortId,
        documentVersionShortId: versionShortId,
        shareIdentifier,
      },
      skip: !enabled,
    }),
    {
      retryIf: result => {
        if (!enabled) return false
        const artboard = getArtboard(result)
        const files = (artboard && artboard.files) || []
        return files.some(file => !file?.url)
      },
    }
  )

const getPagination = (
  version: VersionFragment | undefined,
  artboard: ArtboardDetailInfoFragment | undefined
): Pagination | undefined => {
  if (!version || !artboard) return undefined
  const total = version.document?.artboardCount
  const current =
    artboard.documentOrder == null ? undefined : artboard.documentOrder

  return { current, total }
}

interface DetailViewQueryProps
  extends RouteComponentProps<
    Partial<RouteParams<'ARTBOARD_REVISION'>> & RouteParams<'ARTBOARD_DETAIL'>
  > {}

export const DetailViewQuery = ({ match }: DetailViewQueryProps) => {
  const { permanentArtboardShortId, revisionDocShortId } = match.params

  const versionContext = useVersioning()
  const { onCurrentRevisionChange } = useOnCurrentRevisionChange()
  const { versionShortId, share } = versionContext
  const { userCanAccessVersions } = share

  const isViewingSpecificRevision = !!revisionDocShortId

  const shouldFetchArtboardAtRev =
    userCanAccessVersions || isViewingSpecificRevision

  const artboardAtDocResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    versionShortId,
    shareIdentifier: share.identifier,
    enabled: true,
  })

  const artboardAtRevResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    versionShortId: revisionDocShortId || versionShortId,
    shareIdentifier: share.identifier,
    enabled: shouldFetchArtboardAtRev,
  })

  const artboardAtLatestResult = useRetryGetDetailViewArtboardQuery({
    permanentArtboardShortId,
    /*
     * passing latestVersionId does not work
     * as on the latest version of the document the artboard might be deleted
     * leaving this comment here, so that if anyone would
     * try to optimise it in the future, they would be aware
     * more: https://github.com/sketch-hq/cloud-frontend/pull/800#discussion_r289356950
     */
    // versionId={latestVersionId}
    shareIdentifier: share.identifier,
    enabled: userCanAccessVersions,
  })

  const error =
    artboardAtDocResult.error ||
    artboardAtRevResult.error ||
    artboardAtLatestResult.error

  const loading =
    artboardAtDocResult.loading ||
    artboardAtRevResult.loading ||
    artboardAtLatestResult.loading

  if (isUpgradeToLatestNeeded(error)) {
    artboardAtLatestResult.refetch()
    return <UpgradeToLatestVersion error={error} />
  }

  const artboards = {
    docVersion: getArtboard(artboardAtDocResult),
    revVersion: userCanAccessVersions
      ? getArtboard(artboardAtRevResult)
      : getArtboard(artboardAtDocResult),
    latest: userCanAccessVersions
      ? getArtboard(artboardAtLatestResult)
      : getArtboard(artboardAtDocResult),
  }

  // if there is no latest revision or artboard passed,
  // we are assuming that we are looking at the latest version
  const isViewingLatestVersion =
    !artboards.latest ||
    !artboards.revVersion ||
    artboards.latest.revisionIdentifier ===
      artboards.revVersion.revisionIdentifier

  const isPrototypePlayEnabled =
    !revisionDocShortId ||
    (!!artboards.revVersion &&
      artboards.revVersion?.revisionIdentifier ===
        artboards.docVersion?.revisionIdentifier)

  const { currentVersion } = (!versionContext.loading && versionContext) || {}

  const pagination = getPagination(currentVersion, artboards.docVersion)
  const artboardIdAtLatestVersion =
    artboards.latest?.documentVersionShortId || undefined

  const onRevisionChange = (data: RevisionChangedProps) => {
    /**
     * In all cases when revision changes (moved, deleted, created)
     * it is possible to find a case that latest revision also
     * has changed. Therefore it is a good idea, always refetch
     * the latest revision.
     *
     * Also see https://github.com/sketch-hq/Cloud/issues/1392
     */
    artboardAtLatestResult.refetch()
    if (data.selectedRevisionChange) {
      onCurrentRevisionChange(data)
    }
  }

  return (
    <DetailView
      share={share}
      currentVersion={currentVersion}
      artboard={artboards.revVersion}
      isViewingLatestVersion={isViewingLatestVersion}
      isPrototypePlayEnabled={isPrototypePlayEnabled}
      artboardIdAtLatestVersion={artboardIdAtLatestVersion}
      pagination={pagination}
      onRevisionChange={onRevisionChange}
      error={error}
      loading={loading}
    />
  )
}
